import { z } from 'zod'

// =====================
// Metric Types
// =====================

export const MetricTypeEnum = {
  INVENTORY_TURNS: 'INVENTORY_TURNS',
  UTILIZATION: 'UTILIZATION',
  CONTACT_TO_CASH: 'CONTACT_TO_CASH',
  REVENUE: 'REVENUE',
  CAPACITY: 'CAPACITY',
} as const

export type MetricType = (typeof MetricTypeEnum)[keyof typeof MetricTypeEnum]

export const AnalyticsPeriodTypeEnum = {
  DAY: 'DAY',
  WEEK: 'WEEK',
  MONTH: 'MONTH',
  QUARTER: 'QUARTER',
  YEAR: 'YEAR',
} as const

export type AnalyticsPeriodType =
  (typeof AnalyticsPeriodTypeEnum)[keyof typeof AnalyticsPeriodTypeEnum]

// =====================
// Inventory Turns
// =====================

export interface InventoryTurnsResult {
  inventoryTurns: number
  annualizedCOGS: number
  averageInventoryValue: number
  daysOfInventory: number
  periodMonths: number
  calculatedAt: Date
}

export interface CategoryTurnsResult extends InventoryTurnsResult {
  categoryId: string
  categoryName: string
  categoryType: string
}

export interface InventoryTurnsOptions {
  categoryId?: string
  locationId?: string
  periodMonths?: number
}

// =====================
// Inventory Analytics Responses
// =====================

/** Movement totals for one calendar day (UTC). */
export interface InventoryMovementDailyStat {
  /** YYYY-MM-DD */
  date: string
  totalMovements: number
  /** Units received (IN movements). */
  inQuantity: number
  /** Units shipped or consumed (OUT movements). */
  outQuantity: number
  /** Net signed change from ADJUSTMENT movements. */
  adjustments: number
  /** Units moved between locations; item-level stock is unchanged. */
  transfers: number
}

export interface InventoryMovementHistoryResult {
  /** One entry per day in the window, oldest first, including empty days. */
  dailyStats: InventoryMovementDailyStat[]
  /** Movement count per type (IN, OUT, ADJUSTMENT, TRANSFER). */
  typeStats: Record<string, number>
  /** Movement count per reason. */
  reasonStats: Record<string, number>
  /** Movement count and summed quantity per category type. */
  categoryStats: Record<
    string,
    { totalMovements: number; totalQuantity: number }
  >
  totalMovements: number
  period: { startDate: string; endDate: string; days: number }
}

export interface InventoryTopMovingItem {
  /** Missing only if the item was removed after the movement was counted. */
  item?: {
    id: string
    name: string
    sku: string
    quantity: number
    category: { name: string; type: string } | null
  }
  movementCount: number
  /** Sum of movement quantities (adjustments are signed). */
  totalQuantityMoved: number
}

export interface InventoryValueCategoryBreakdown {
  /** Null when any nonzero holding has no known cost. */
  totalCostValue: number | null
  /** Null when any nonzero holding has no known selling price. */
  totalRetailValue: number | null
  /** Units on hand. */
  totalItems: number
  /** Distinct items. */
  itemCount: number
}

export interface InventoryValueSummaryResult {
  summary: {
    /** Null when any nonzero holding has no known cost. */
    totalCostValue: number | null
    /** Null when any nonzero holding has no known selling price. */
    totalRetailValue: number | null
    /** Null unless both complete totals are known. */
    totalMargin: number | null
    /** Null unless margin is known and retail value is positive. */
    marginPercentage: number | null
    /** Units on hand across all items. */
    totalItems: number
    /** Distinct (non-deleted) items. */
    uniqueItems: number
  }
  /** Keyed by category type. */
  categoryBreakdown: Record<string, InventoryValueCategoryBreakdown>
}

export interface InventoryLowStockItem {
  id: string
  name: string
  sku: string
  quantity: number
  minQuantity: number
  reorderQuantity: number | null
  category: { id: string; name: string; type: string } | null
  /** Current quantity as a percent of the minimum. */
  stockLevel: number
  /** Units needed to reach the minimum. */
  shortfall: number
}

// =====================
// Inventory Stock Health
// =====================

/** Default look-back window for slow-moving and category metrics. */
export const INVENTORY_STOCK_HEALTH_DEFAULT_DAYS = 90

/** Rows returned per list; counts always cover every matching item. */
export const INVENTORY_STOCK_HEALTH_LIST_LIMIT = 50

export interface InventoryStockHealthItem {
  id: string
  name: string
  sku: string
  quantity: number
}

export interface InventorySlowMovingItem extends InventoryStockHealthItem {
  /** Most recent outbound movement ever, or null if it never shipped. */
  lastOutboundDate: string | null
}

export interface InventoryOverstockItem extends InventoryStockHealthItem {
  maxQuantity: number
  excessQuantity: number
}

/**
 * Item counts per stock state. Each active item lands in exactly one bucket,
 * checked in order: out of stock (quantity <= 0), low (at or below its
 * minimum), overstocked (above its maximum), otherwise within levels.
 */
export interface InventoryStockLevelCounts {
  outOfStock: number
  low: number
  overstocked: number
  withinLevels: number
}

export interface InventoryCategoryPerformance {
  categoryId: string
  categoryName: string
  itemCount: number
  outboundQuantity: number
  outboundValue: number
  /** Percent (0–100) of all outbound units in the window. */
  quantitySharePercent: number
  /** Percent (0–100) of all outbound cost value, or null when none is costed. */
  valueSharePercent: number | null
}

export interface InventoryStockHealthResult {
  windowDays: number
  windowStart: string
  calculatedAt: string
  slowMoving: { count: number; items: InventorySlowMovingItem[] }
  overstock: {
    count: number
    /** Active items that have a maximum quantity set. */
    itemsWithMaxQuantity: number
    items: InventoryOverstockItem[]
  }
  stockLevels: InventoryStockLevelCounts
  categoryPerformance: InventoryCategoryPerformance[]
  totalOutboundQuantity: number
  totalOutboundValue: number
}

// =====================
// Utilization Forecast
// =====================

export interface ForecastPeriod {
  periodStart: Date
  predictedUtilization: number
  lowerBound: number
  upperBound: number
  confidence: number
  trend: 'up' | 'down' | 'stable'
}

export interface UtilizationForecast {
  organizationId: string
  memberId?: string
  basedOnWeeks: number
  forecastWeeks: number
  currentUtilization: number
  averageUtilization: number
  trendDirection: 'increasing' | 'decreasing' | 'stable'
  predictions: ForecastPeriod[]
  generatedAt: Date
}

export interface UtilizationForecastOptions {
  memberId?: string
  forecastWeeks?: number
  basedOnWeeks?: number
}

// =====================
// Contact-to-Cash Funnel
// =====================

export interface ContactMetrics {
  created: number
  qualified: number
}

export interface DealMetrics {
  created: number
  won: number
  totalValue: number
  wonValue: number
}

export interface ContractMetrics {
  created: number
  signed: number
  totalValue: number
  signedValue: number
  pendingValue: number
}

export interface DeliverableFunnelMetrics {
  created: number
  accepted: number
  completed: number
  linkedContracts: number
  linkedProjects: number
  totalValue: number
  acceptedValue: number
}

export interface ProjectDeliveryMetrics {
  linkedProjects: number
  projectsWithActuals: number
  timeEntriesLogged: number
  actualHours: number
  billableHours: number
  billableValue: number
}

export interface InvoiceMetrics {
  created: number
  sent: number
  paid: number
  totalValue: number
  paidValue: number
}

export interface PaymentMetrics {
  count: number
  received: number
}

export interface FunnelConversions {
  contactToQualified: number
  qualifiedToDeal: number
  dealToWon: number
  wonToContract: number
  contractToSigned: number
  signedToDeliverable: number
  deliverableToProject: number
  projectToInvoice: number
  signedToInvoice: number
  invoiceToPaid: number
  overallContactToCash: number
}

export interface FunnelCycleTimes {
  contactToQualified: number
  qualifiedToDeal: number
  dealToContract: number
  contractToDeliverable: number
  deliverableToProject: number
  projectToInvoice: number
  contractToInvoice: number
  invoiceToPaid: number
  totalContactToCash: number
}

export interface ContactToCashFunnelResult {
  periodStart: Date
  periodEnd: Date
  funnel: {
    contacts: ContactMetrics
    deals: DealMetrics
    contracts: ContractMetrics
    deliverables: DeliverableFunnelMetrics
    projectDelivery: ProjectDeliveryMetrics
    invoices: InvoiceMetrics
    payments: PaymentMetrics
  }
  conversions: FunnelConversions
  totalPipelineValue: number
  totalRevenue: number
  averageDaysToClose: FunnelCycleTimes
  calculatedAt: Date
}

export interface ContactToCashFunnelOptions {
  periodStart?: Date
  periodEnd?: Date
}

// =====================
// Dashboard
// =====================

export const WidgetTypeEnum = {
  METRIC_CARD: 'metric_card',
  CHART: 'chart',
  TABLE: 'table',
  FUNNEL: 'funnel',
  GAUGE: 'gauge',
  TREND: 'trend',
  LIST: 'list',
} as const

export type WidgetType = (typeof WidgetTypeEnum)[keyof typeof WidgetTypeEnum]

export const ChartTypeEnum = {
  LINE: 'line',
  BAR: 'bar',
  AREA: 'area',
  PIE: 'pie',
  DONUT: 'donut',
  STACKED_BAR: 'stacked_bar',
  COMPOSED: 'composed',
} as const

export type ChartType = (typeof ChartTypeEnum)[keyof typeof ChartTypeEnum]

export interface WidgetConfig {
  dataSource: string
  metricType?: string
  chartType?: ChartType
  filters?: Record<string, unknown>
  colors?: string[]
  showLegend?: boolean
  showTrend?: boolean
  showLabels?: boolean
  periodType?: AnalyticsPeriodType
  timeRange?: number // days
}

export interface DashboardWidget {
  id: string
  dashboardId: string
  widgetType: WidgetType
  moduleType?: string
  title: string
  config: WidgetConfig
  x: number
  y: number
  width: number
  height: number
  createdAt: Date
  updatedAt: Date
}

export interface Dashboard {
  id: string
  organizationId: string
  createdById: string
  name: string
  description?: string
  isDefault: boolean
  isShared: boolean
  roleAccess: string[]
  layout: unknown[]
  filters: Record<string, unknown>
  refreshRate?: number
  widgets: DashboardWidget[]
  createdAt: Date
  updatedAt: Date
}

export interface CreateDashboardDto {
  name: string
  description?: string
  isDefault?: boolean
  isShared?: boolean
  roleAccess?: string[]
  layout?: unknown[]
  filters?: Record<string, unknown>
  refreshRate?: number
}

export interface UpdateDashboardDto extends Partial<CreateDashboardDto> {}

export interface CreateWidgetDto {
  widgetType: WidgetType
  moduleType?: string
  title: string
  config: WidgetConfig
  x?: number
  y?: number
  width?: number
  height?: number
}

export interface WidgetPosition {
  id: string
  x: number
  y: number
  width: number
  height: number
}

// =====================
// Reports
// =====================

export const ReportTypeEnum = {
  INVENTORY: 'inventory',
  CRM: 'crm',
  UTILIZATION: 'utilization',
  FUNNEL: 'funnel',
  TIME: 'time',
  PROJECT: 'project',
  FINANCIAL: 'financial',
} as const

export type ReportType = (typeof ReportTypeEnum)[keyof typeof ReportTypeEnum]

export const ReportFormatEnum = {
  PDF: 'PDF',
  CSV: 'CSV',
  XLSX: 'XLSX',
} as const

export type ReportFormat =
  (typeof ReportFormatEnum)[keyof typeof ReportFormatEnum]

export interface ReportConfig {
  columns: string[]
  groupBy?: string[]
  aggregations?: Record<string, 'sum' | 'avg' | 'count' | 'min' | 'max'>
  sortBy?: { field: string; direction: 'asc' | 'desc' }[]
  chartType?: ChartType
  showTotals?: boolean
}

export interface SavedReport {
  id: string
  organizationId: string
  createdById: string
  name: string
  description?: string
  reportType: ReportType
  config: ReportConfig
  filters: Record<string, unknown>
  scheduleEnabled: boolean
  scheduleCron?: string
  scheduleFormat?: ReportFormat
  scheduleRecipients: string[]
  lastRunAt?: Date
  nextRunAt?: Date
  createdAt: Date
  updatedAt: Date
}

export interface CreateReportDto {
  name: string
  description?: string
  reportType: ReportType
  config: ReportConfig
  filters?: Record<string, unknown>
  scheduleEnabled?: boolean
  scheduleCron?: string
  scheduleFormat?: ReportFormat
  scheduleRecipients?: string[]
}

export interface UpdateReportDto extends Partial<CreateReportDto> {}

export interface ReportResult {
  reportId: string
  reportName: string
  reportType: ReportType
  generatedAt: Date
  filters: Record<string, unknown>
  columns: string[]
  data: Record<string, unknown>[]
  totals?: Record<string, number>
  chartData?: unknown
}

/**
 * Outcome of one scheduled-report delivery attempt. Mirrors
 * `DeliveryOutcome` in the API's ScheduledReportService — clients render the
 * ad-hoc "Send now" result from these, so the two must stay in step.
 *
 * `duplicate` is a success, not a failure: the delivery ledger is keyed by
 * minute slot, so a double-click inside the same minute is absorbed rather
 * than sending the report twice.
 */
export const ReportDeliveryOutcomeEnum = {
  SENT: 'sent',
  DUPLICATE: 'duplicate',
  NO_RECIPIENTS: 'no-recipients',
  FAILED: 'failed',
  INVALID_SCHEDULE: 'invalid-schedule',
} as const

export type ReportDeliveryOutcome =
  (typeof ReportDeliveryOutcomeEnum)[keyof typeof ReportDeliveryOutcomeEnum]

/** Response of `POST /analytics/reports/:reportId/send-now`. */
export interface SendReportNowResult {
  outcome: ReportDeliveryOutcome
  /** How many addresses the send was fanned out to. */
  recipients: number
}

/**
 * Response of `POST /analytics/alerts/rules/:ruleId/evaluate` — the same
 * tallies the scheduled sweep reports, scoped to the single rule.
 */
export interface AlertEvaluationResult {
  rulesEvaluated: number
  subjectsEvaluated: number
  alertsCreated: number
  unsnoozed: number
  failed: number
}

// =====================
// Alerts
// =====================

export const AlertTypeEnum = {
  LOW_STOCK: 'low_stock',
  RESOURCE_CONFLICT: 'resource_conflict',
  CAPACITY_SHORTAGE: 'capacity_shortage',
  OVERSTOCK: 'overstock',
  DEADLINE_RISK: 'deadline_risk',
  BUDGET_VARIANCE: 'budget_variance',
  OVERDUE_INVOICE: 'overdue_invoice',
  UTILIZATION_LOW: 'utilization_low',
  UTILIZATION_HIGH: 'utilization_high',
  CLIENT_HEALTH_RISK: 'client_health_risk',
} as const

export type AlertType = (typeof AlertTypeEnum)[keyof typeof AlertTypeEnum]

/**
 * Module types an alert rule can actually be evaluated against.
 *
 * `AlertRule.moduleType` is free text, and both clients used to offer every
 * ModuleTypeEnum value — but the evaluator can only load subjects for these
 * six, so a rule on anything else is silently skipped forever. The rule
 * editors offer these; a rule that already holds something else (including the
 * aliases the evaluator still accepts — RESOURCES/MEMBER for TEAM, CRM
 * for DEAL, FINANCIALS for INVOICE) keeps its value when edited.
 *
 * `AlertEvaluationService` types its subject loaders as a Record over this
 * union, so adding a member here fails to compile until it can be evaluated.
 */
export const ALERT_SUBJECT_MODULE_TYPES = [
  'INVENTORY',
  'TEAM',
  'INVOICE',
  'ENTITY',
  'PROJECT',
  'DEAL',
] as const

export type AlertSubjectModuleType = (typeof ALERT_SUBJECT_MODULE_TYPES)[number]

export const AlertPriorityEnum = {
  CRITICAL: 'critical',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
} as const

export type AlertPriority =
  (typeof AlertPriorityEnum)[keyof typeof AlertPriorityEnum]

export const AlertFrequencyEnum = {
  REALTIME: 'realtime',
  HOURLY: 'hourly',
  DAILY: 'daily',
  WEEKLY: 'weekly',
} as const

export type AlertFrequency =
  (typeof AlertFrequencyEnum)[keyof typeof AlertFrequencyEnum]

export const AlertStatusEnum = {
  ACTIVE: 'active',
  ACKNOWLEDGED: 'acknowledged',
  SNOOZED: 'snoozed',
  RESOLVED: 'resolved',
} as const

export type AlertStatus = (typeof AlertStatusEnum)[keyof typeof AlertStatusEnum]

export const AlertConditionOperatorEnum = {
  LT: 'lt',
  LTE: 'lte',
  GT: 'gt',
  GTE: 'gte',
  EQ: 'eq',
  NEQ: 'neq',
  CONTAINS: 'contains',
  NOT_CONTAINS: 'not_contains',
} as const

export type AlertConditionOperator =
  (typeof AlertConditionOperatorEnum)[keyof typeof AlertConditionOperatorEnum]

export interface AlertCondition {
  field: string
  operator: AlertConditionOperator
  value: unknown
  logicalOperator?: 'AND' | 'OR'
}

export const AlertActionTypeEnum = {
  EMAIL: 'email',
  SMS: 'sms',
  PUSH: 'push',
  SLACK: 'slack',
  WEBHOOK: 'webhook',
  CREATE_TASK: 'create_task',
} as const

export type AlertActionType =
  (typeof AlertActionTypeEnum)[keyof typeof AlertActionTypeEnum]

export interface AlertAction {
  type: AlertActionType
  config: Record<string, unknown>
}

export interface AlertRule {
  id: string
  organizationId: string
  createdById: string
  name: string
  alertType: AlertType
  moduleType: string
  conditions: AlertCondition[]
  actions: AlertAction[]
  isActive: boolean
  priority: AlertPriority
  frequency: AlertFrequency
  lastTriggered?: Date
  triggerCount: number
  createdAt: Date
  updatedAt: Date
}

export interface ActiveAlert {
  id: string
  ruleId: string
  title: string
  message: string
  metadata: Record<string, unknown>
  status: AlertStatus
  priority: AlertPriority
  assignedToId?: string
  acknowledgedAt?: Date
  acknowledgedBy?: string
  snoozedUntil?: Date
  resolvedAt?: Date
  resolvedBy?: string
  resolution?: string
  createdAt: Date
  updatedAt: Date
  rule?: AlertRule
}

export interface CreateAlertRuleDto {
  name: string
  alertType: AlertType
  moduleType: string
  conditions: AlertCondition[]
  actions: AlertAction[]
  isActive?: boolean
  priority?: AlertPriority
  frequency?: AlertFrequency
}

export interface UpdateAlertRuleDto extends Partial<CreateAlertRuleDto> {}

// =====================
// Zod Schemas for Validation
// =====================

export const CreateDashboardSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().optional(),
  isDefault: z.boolean().optional(),
  isShared: z.boolean().optional(),
  roleAccess: z.array(z.string()).optional(),
  layout: z.array(z.unknown()).optional(),
  filters: z.record(z.unknown()).optional(),
  refreshRate: z.number().min(5).max(3600).optional(),
})

export const CreateWidgetSchema = z.object({
  widgetType: z.enum([
    'metric_card',
    'chart',
    'table',
    'funnel',
    'gauge',
    'trend',
    'list',
  ]),
  moduleType: z.string().optional(),
  title: z.string().min(1).max(100),
  config: z.object({
    dataSource: z.string(),
    metricType: z.string().optional(),
    chartType: z
      .enum(['line', 'bar', 'area', 'pie', 'donut', 'stacked_bar', 'composed'])
      .optional(),
    filters: z.record(z.unknown()).optional(),
    colors: z.array(z.string()).optional(),
    showLegend: z.boolean().optional(),
    showTrend: z.boolean().optional(),
    showLabels: z.boolean().optional(),
    periodType: z.enum(['DAY', 'WEEK', 'MONTH', 'QUARTER', 'YEAR']).optional(),
    timeRange: z.number().optional(),
  }),
  x: z.number().optional(),
  y: z.number().optional(),
  width: z.number().min(1).max(12).optional(),
  height: z.number().min(1).max(12).optional(),
})

export const CreateReportSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().optional(),
  reportType: z.enum([
    'inventory',
    'crm',
    'utilization',
    'funnel',
    'time',
    'project',
    'financial',
  ]),
  config: z.object({
    columns: z.array(z.string()),
    groupBy: z.array(z.string()).optional(),
    aggregations: z
      .record(z.enum(['sum', 'avg', 'count', 'min', 'max']))
      .optional(),
    sortBy: z
      .array(
        z.object({
          field: z.string(),
          direction: z.enum(['asc', 'desc']),
        }),
      )
      .optional(),
    chartType: z
      .enum(['line', 'bar', 'area', 'pie', 'donut', 'stacked_bar', 'composed'])
      .optional(),
    showTotals: z.boolean().optional(),
  }),
  filters: z.record(z.unknown()).optional(),
  scheduleEnabled: z.boolean().optional(),
  scheduleCron: z.string().optional(),
  scheduleFormat: z.enum(['PDF', 'CSV', 'XLSX']).optional(),
  scheduleRecipients: z.array(z.string().email()).optional(),
})

export const CreateAlertRuleSchema = z.object({
  name: z.string().min(1).max(100),
  alertType: z.enum([
    'low_stock',
    'resource_conflict',
    'capacity_shortage',
    'overstock',
    'deadline_risk',
    'budget_variance',
    'overdue_invoice',
    'utilization_low',
    'utilization_high',
    'client_health_risk',
  ]),
  moduleType: z.string(),
  conditions: z.array(
    z.object({
      field: z.string(),
      operator: z.enum([
        'lt',
        'lte',
        'gt',
        'gte',
        'eq',
        'neq',
        'contains',
        'not_contains',
      ]),
      value: z.unknown(),
      logicalOperator: z.enum(['AND', 'OR']).optional(),
    }),
  ),
  actions: z.array(
    z.object({
      type: z.enum(['email', 'sms', 'push', 'slack', 'webhook', 'create_task']),
      config: z.record(z.unknown()),
    }),
  ),
  isActive: z.boolean().optional(),
  priority: z.enum(['critical', 'high', 'medium', 'low']).optional(),
  frequency: z.enum(['realtime', 'hourly', 'daily', 'weekly']).optional(),
})
